import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue, unref } from 'vue';

import { extractBenchmarksObjectFromIndustries } from '@/app/dashboards/utils/benchmarks.utils';
import {
  extractReportType,
  getBrandOccurrenceInfo,
  getComparisonOccurrenceInfo,
  getCompetitorOccurrenceInfo,
  getFilterOccurrenceInfo,
  getMediaTypeOccurrenceInfo,
  getSimplifiedOccurrenceDict,
  getTopicOccurrenceInfo,
} from '@/app/dashboards/utils/dashboard-general.util';
import { CHANNELS } from '@/models/dashboards/channels.enum';

import { REPORT_CATEGORY_TO_FILTER_KEY_MAP } from '../utils/reports.enum';

interface UseDashboardTrackingPropsParams {
  dashboard?: MaybeRefOrGetter<
    { id?: number; name?: string; [key: string]: unknown } | null | undefined
  >;
  reportDateRange?: MaybeRefOrGetter<string[] | undefined>;
  contextDateRange?: MaybeRefOrGetter<string[] | undefined>;
  reports?: MaybeRefOrGetter<Record<string, unknown> | undefined>;
  industries?: MaybeRefOrGetter<unknown[] | undefined>;
  brands?: MaybeRefOrGetter<unknown[] | undefined>;
  competitors?: MaybeRefOrGetter<unknown[] | undefined>;
  topics?: MaybeRefOrGetter<unknown[] | undefined>;
  comparisons?: MaybeRefOrGetter<unknown[] | undefined>;
}

function getAdAccountOccurrenceInfo(
  occurrences: Record<string, Record<string, { id: string; count: number }>>,
  report: Record<string, unknown>,
) {
  const meta = report?.meta as Record<string, unknown> | undefined;
  const ids = (meta?.source_ad_account_ids as string[]) || [];
  const channel = (meta?.channels as string[])?.[0];
  ids.forEach((id: string) => {
    if (!occurrences?.[channel]?.[id]) {
      occurrences[channel][id] = {
        id,
        count: 1,
      };
    } else {
      occurrences[channel][id].count += 1;
    }
  });
}

function getAdCampaignOccurrenceInfo(
  occurrences: Record<string, Record<string, { id: string; count: number }>>,
  report: Record<string, unknown>,
) {
  const meta = report?.meta as Record<string, unknown> | undefined;
  const ids = (meta?.source_campaign_ids as string[]) || [];
  const channel = (meta?.channels as string[])?.[0];
  ids.forEach((id: string) => {
    if (!occurrences?.[channel]?.[id]) {
      occurrences[channel][id] = {
        id,
        count: 1,
      };
    } else {
      occurrences[channel][id].count += 1;
    }
  });
}

export function useDashboardTrackingProps({
  dashboard,
  reportDateRange,
  contextDateRange,
  reports,
  industries,
  brands,
  competitors,
  topics,
  comparisons,
}: UseDashboardTrackingPropsParams = {}) {
  const reportsList = Object.values(unref(reports) ?? []).filter(
    (report) =>
      report.dashboard_id === (unref(dashboard) as Record<string, unknown> | undefined)?.id,
  );
  const brandOccurrences: Record<string, unknown> = {};
  const competitorOccurrences: Record<string, unknown> = {};
  const channelsOccurrences: Record<string, number> = {};
  const reportTypesOccurrences: Record<string, number> = {};
  const aggregateByOccurrences: Record<string, number> = {};
  const mediaTypeMetricsOccurrences: Record<string, unknown> = {};
  const baseMetricOccurrences: Record<string, unknown> = {};
  const mediaTypeOccurrences: Record<string, unknown> = {};
  const benchmarksOccurrences: Record<string, number> = {};
  const adAccountOccurrences: Record<string, Record<string, { id: string; count: number }>> = {
    [CHANNELS.META_ADS.value]: {},
    [CHANNELS.TIKTOK_ADS.value]: {},
  };
  const adCampaignOccurrences: Record<string, Record<string, { id: string; count: number }>> = {
    [CHANNELS.META_ADS.value]: {},
    [CHANNELS.TIKTOK_ADS.value]: {},
  };
  const topicsOccurrences: Record<string, unknown> = {};
  const comparisonsOccurrences: Record<string, unknown> = {};
  const filterOccurrences: Record<string, unknown> = {};
  const reportIdsIncluded: unknown[] = [];
  let numberReportsWithBenchmarks = 0;
  let totalNumberOfBenchmarks = 0;
  let numberOfReportsWithCompetitors = 0;
  let totalNumberOfCompetitors = 0;
  let numberOfReportsWithStaticDates = 0;
  let numberOfReportsWithAds = 0;

  reportsList.forEach((report) => {
    const meta = report?.meta as Record<string, unknown> | undefined;
    const listening = meta?.listening as Record<string, unknown> | undefined;
    const reportType = (report?.type as string) || '';
    const aggregateBy = (meta?.aggregate_by as string) || '';
    const brandIds = (meta?.brand_ids as unknown[]) || [];
    const channels =
      (meta?.channels as string[]) || ([] as string[]).concat((meta?.channel as string) || []);
    const metrics =
      (meta?.metrics as string[]) || ([] as string[]).concat((meta?.metric as string) || []);
    const topicIds = (listening?.topic_ids as unknown[]) || [];
    const comparisonTopicIds = (listening?.comparison_topic_ids as unknown[]) || [];
    const reportFilters = Object.values(REPORT_CATEGORY_TO_FILTER_KEY_MAP).reduce<
      Record<string, unknown>[]
    >((acc, filterKey) => {
      const filterSection = meta?.[filterKey] as Record<string, unknown> | undefined;
      const filters = (filterSection?.filters as Record<string, unknown>) || {};
      if (Object.keys(filters).length > 0) {
        acc.push(filters);
      }
      return acc;
    }, []);

    reportIdsIncluded.push(report.id);

    if (meta?.static_dates) {
      numberOfReportsWithStaticDates += 1;
    }

    const benchmarks = (meta?.benchmarks as unknown[]) || [];
    const benchmarksNames = extractBenchmarksObjectFromIndustries(benchmarks, unref(industries));
    if (benchmarksNames.length > 0) {
      numberReportsWithBenchmarks += 1;
    }
    benchmarksNames.forEach((benchmark: Record<string, unknown>) => {
      totalNumberOfBenchmarks += 1;
      const formattedName = (benchmark.name as string).replace(':', ' -');
      benchmarksOccurrences[formattedName] = (benchmarksOccurrences?.[formattedName] || 0) + 1;
    });

    if (reportType) {
      const updatedReportType = extractReportType(reportType, brandIds, metrics, channels);
      reportTypesOccurrences[updatedReportType] =
        (reportTypesOccurrences?.[updatedReportType] || 0) + 1;
    }

    if (aggregateBy) {
      aggregateByOccurrences[aggregateBy] = (aggregateByOccurrences?.[aggregateBy] ?? 0) + 1;
    }

    channels.forEach((reportChannel: string) => {
      if (reportChannel) {
        channelsOccurrences[reportChannel] = (channelsOccurrences?.[reportChannel] || 0) + 1;
      }
    });

    getMediaTypeOccurrenceInfo(
      mediaTypeOccurrences,
      mediaTypeMetricsOccurrences,
      baseMetricOccurrences,
      metrics,
      channels,
      reportType,
    );

    getBrandOccurrenceInfo(brandOccurrences, report, unref(brands));
    const competitorsVal = toValue(competitors);
    if (competitorsVal && competitorsVal.length > 0) {
      numberOfReportsWithCompetitors += 1;
      totalNumberOfCompetitors += competitorsVal.length;
    }
    getCompetitorOccurrenceInfo(competitorOccurrences, report, unref(competitors));

    if (((meta?.source_ad_account_ids as unknown[] | undefined)?.length ?? 0) > 0) {
      numberOfReportsWithAds += 1;
    }
    getAdAccountOccurrenceInfo(adAccountOccurrences, report);
    getAdCampaignOccurrenceInfo(adCampaignOccurrences, report);
    getTopicOccurrenceInfo(topicsOccurrences, topicIds, unref(topics));
    getComparisonOccurrenceInfo(comparisonsOccurrences, comparisonTopicIds, unref(comparisons));
    getFilterOccurrenceInfo(filterOccurrences, reportFilters);
  });
  const brandIdOccurrences = getSimplifiedOccurrenceDict(brandOccurrences, 'id');
  const brandNameOccurrences = getSimplifiedOccurrenceDict(brandOccurrences, 'name');
  const competitorIdOccurrences = getSimplifiedOccurrenceDict(competitorOccurrences, 'id');
  const competitorHandleOccurrences = getSimplifiedOccurrenceDict(competitorOccurrences, 'handle');
  const topicIdOccurrences = getSimplifiedOccurrenceDict(topicsOccurrences, 'id');
  const topicNameOccurrences = getSimplifiedOccurrenceDict(topicsOccurrences, 'name');
  const comparisonIdOccurrences = getSimplifiedOccurrenceDict(comparisonsOccurrences, 'id');
  const comparisonNameOccurrences = getSimplifiedOccurrenceDict(comparisonsOccurrences, 'name');

  const metaAdAccountIdOccurrences = getSimplifiedOccurrenceDict(
    adAccountOccurrences.META_ADS,
    'id',
  );
  const tikTokAdAccountIdOccurrences = getSimplifiedOccurrenceDict(
    adAccountOccurrences.TIKTOK_ADS,
    'id',
  );
  const metaAdCampaignIdOccurrences = getSimplifiedOccurrenceDict(
    adCampaignOccurrences.META_ADS,
    'id',
  );
  const tikTokAdCampaignIdOccurrences = getSimplifiedOccurrenceDict(
    adCampaignOccurrences.TIKTOK_ADS,
    'id',
  );

  const dashboardTrackingProps = computed(() => {
    const results: Record<string, unknown> = {};
    if (unref(dashboard)) {
      const dashboardVal = unref(dashboard) as Record<string, unknown>;
      results.dashboardName = dashboardVal.name;
      results.dashboardId = dashboardVal.id;
    }

    const reportDateRangeVal = toValue(reportDateRange);
    if (reportDateRangeVal) {
      results.dashboardStartDate = reportDateRangeVal[0];
      results.dashboardEndDate = reportDateRangeVal[1];
    }

    const contextDateRangeVal = toValue(contextDateRange);
    if (contextDateRangeVal) {
      results.dashboardContextStartDate = contextDateRangeVal[0];
      results.dashboardContextEndDate = contextDateRangeVal[1];
    }

    if (unref(reports)) {
      results.numberOfReports = reportsList.length;
      results.reportTypesIncluded = Object.keys(reportTypesOccurrences);
      results.reportTypesAmounts = reportTypesOccurrences;
      results.reportAggregateByIncluded = Object.keys(aggregateByOccurrences);
      results.reportAggregateByAmounts = aggregateByOccurrences;
      results.numberOfReportsWithStaticDates = numberOfReportsWithStaticDates;
      results.reportIdsIncluded = reportIdsIncluded;
      results.metricsIncluded = Object.keys(baseMetricOccurrences);
      results.metricsAmounts = baseMetricOccurrences;
      results.reportMetricsMediaTypeMetricsIncluded = Object.keys(mediaTypeMetricsOccurrences);
      results.reportMetricsMediaTypeMetricsAmounts = mediaTypeMetricsOccurrences;
      results.channelsIncluded = Object.keys(channelsOccurrences);
      results.channelsAmounts = channelsOccurrences;
      results.reportMetricsMediaTypeIncluded = Object.keys(mediaTypeOccurrences);
      results.reportMetricsMediaTypeAmounts = mediaTypeOccurrences;

      if (unref(brands)) {
        results.brandIdsIncluded = Object.keys(brandIdOccurrences);
        results.brandIdsAmounts = brandIdOccurrences;
        results.brandNamesIncluded = Object.keys(brandNameOccurrences);
        results.brandNamesAmounts = brandNameOccurrences;
      }

      if (unref(competitors)) {
        results.totalNumberOfCompetitors = totalNumberOfCompetitors;
        results.numberOfReportsWithCompetitors = numberOfReportsWithCompetitors;
        results.competitorIdsIncluded = Object.keys(competitorIdOccurrences);
        results.competitorIdsAmounts = competitorIdOccurrences;
        results.competitorHandlesIncluded = Object.keys(competitorHandleOccurrences);
        results.competitorHandlesAmounts = competitorHandleOccurrences;
      }

      if (unref(industries)) {
        results.benchmarksIncluded = Object.keys(benchmarksOccurrences);
        results.benchmarksAmounts = benchmarksOccurrences;
        results.numberOfReportsWithBenchmarks = numberReportsWithBenchmarks;
        results.totalNumberOfBenchmarks = totalNumberOfBenchmarks;
      }

      if (unref(topics)) {
        results.topicIdsIncluded = Object.keys(topicIdOccurrences);
        results.topicIdsAmounts = topicIdOccurrences;
        results.topicNamesIncluded = Object.keys(topicNameOccurrences);
        results.topicNamesAmounts = topicNameOccurrences;
      }

      if (unref(comparisons)) {
        results.comparisonIdsIncluded = Object.keys(comparisonIdOccurrences);
        results.comparisonIdsAmounts = comparisonIdOccurrences;
        results.comparisonNamesIncluded = Object.keys(comparisonNameOccurrences);
        results.comparisonNamesAmounts = comparisonNameOccurrences;
      }

      if (Object.keys(filterOccurrences).length > 0) {
        results.filterOccurrences = getSimplifiedOccurrenceDict(filterOccurrences, 'id');
        results.filterOccurrencesValues = Object.entries(filterOccurrences).map(([key, value]) => ({
          [key]: (value as Record<string, unknown>).value,
        }));
      }

      results.numberOfReportsWithAds = numberOfReportsWithAds;
      results.metaAdAccountIdsIncluded = Object.keys(metaAdAccountIdOccurrences);
      results.metaAdAccountIdsAmounts = metaAdAccountIdOccurrences;
      results.tikTokAdAccountIdsIncluded = Object.keys(tikTokAdAccountIdOccurrences);
      results.tikTokAdAccountIdsAmounts = tikTokAdAccountIdOccurrences;
      results.metaAdCampaignIdsIncluded = Object.keys(metaAdCampaignIdOccurrences);
      results.metaAdCampaignIdsAmounts = metaAdCampaignIdOccurrences;
      results.tikTokAdCampaignIdsIncluded = Object.keys(tikTokAdCampaignIdOccurrences);
      results.tikTokAdCampaignIdsAmounts = tikTokAdCampaignIdOccurrences;
    }

    return results;
  });

  return {
    dashboardTrackingProps,
  };
}
